'use client'

import { useRouter } from 'next/navigation'
import { useCallback, useMemo } from 'react'
import { toast } from 'react-toastify'
import { getCurrentCartService } from '@/services/domain/cart/CartService'
import { getOutOfStock } from '@/services/domain/cart/cartErrors'
import { useCartStore } from '@/stores/useCartStore'
import type { ICartItem } from '~/schemas/cart/cart'
import { useCartCache } from './useCartCache'

/**
 * Regras de produto fora de estoque (substitui o `OutStockHandlerContext` e o `verifyStockQuantities` da tela do
 * carrinho). O item está fora de estoque quando o produto zerou ou tem menos do que a quantidade do item; o estoque
 * vem atualizado do catálogo no `cart/current`.
 */

const EMPTY: ICartItem[] = []
const OUT_OF_STOCK_MESSAGE = 'Um ou mais produtos que estão no seu carrinho estão sem estoque.'

export const isItemOutOfStock = (item: ICartItem): boolean =>
    item.product.stockQuantity <= 0 || item.product.stockQuantity < item.quantity

export function useOutOfStock() {
    const router = useRouter()
    const { setCartCache } = useCartCache()
    const items: ICartItem[] = useCartStore((state) => state.data?.cartItems) ?? EMPTY

    const outOfStockItems = useMemo(() => items.filter(isItemOutOfStock), [items])

    /**
     * Para o `outStockError` (409) do checkout/finish: avisa com a mensagem da API, revalida o carrinho pelo
     * `cart/current` (estoque atualizado) e volta para /carrinho. Resolve `false` sem fazer nada para outro erro.
     */
    const handleOutOfStockError = useCallback(
        async (error: unknown): Promise<boolean> => {
            const outOfStock = getOutOfStock(error)
            if (!outOfStock) return false
            toast.error(outOfStock.message || OUT_OF_STOCK_MESSAGE, { autoClose: 5000 })
            try {
                setCartCache(await getCurrentCartService())
            } catch {
                // Sem revalidação o carrinho guardado fica; a tela do carrinho revalida ao montar.
            }
            router.push('/carrinho')
            return true
        },
        [setCartCache, router],
    )

    return {
        outOfStockItems,
        hasOutOfStockItems: outOfStockItems.length > 0,
        /** Carrinho com itens e todos com estoque: pode seguir para o checkout. */
        canCheckout: items.length > 0 && outOfStockItems.length === 0,
        outOfStockMessage: OUT_OF_STOCK_MESSAGE,
        handleOutOfStockError,
    }
}
